import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpsertScheduleOverride } from "../../hooks/realestate";
import { useScheduleOverrideBulkEdit } from "../../hooks/useScheduleOverrideBulkEdit";
import { formatMonthShort } from "../../lib/format";
import type { InterestPhase, ScheduleEntry, ScheduleOverride } from "../../lib/realestate";
import { MoneyInput } from "../forms/MoneyInput";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { ScheduleOverrideFormModal } from "./ScheduleOverrideFormModal";

interface Props {
  propertyId: string;
  loanId: string;
  schedule: ScheduleEntry[];
  overrides: ScheduleOverride[];
  interestPhases: InterestPhase[];
}

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", { year: "numeric", month: "2-digit" });
}

function findOverride(
  entry: ScheduleEntry,
  overrides: ScheduleOverride[],
): ScheduleOverride | null {
  const ym = entry.date.slice(0, 7);
  return overrides.find((o) => o.month.slice(0, 7) === ym) ?? null;
}

export function AmortizationTable({
  propertyId,
  loanId,
  schedule,
  overrides,
  interestPhases,
}: Props) {
  const { t } = useTranslation();
  const bulk = useScheduleOverrideBulkEdit({ propertyId, loanId, schedule, overrides });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const upsertOverride = useUpsertScheduleOverride();

  function isPhaseStart(entryYm: string): { idx: number; phase: InterestPhase } | null {
    const sorted = [...interestPhases].sort((a, b) => a.startDate.localeCompare(b.startDate));
    for (let i = 0; i < sorted.length; i++) {
      const candidate = sorted[i];
      if (!candidate) continue;
      const phaseYm = candidate.startDate.slice(0, 7);
      if (phaseYm === entryYm) {
        return { idx: i + 1, phase: candidate };
      }
    }
    return null;
  }

  // Counter: nur counter_partial rendern wenn tatsaechlich Mutations versucht wurden
  // (sonst attemptCount=0 → negative Counter durch Validation-Fail-Pfad).
  const counterText =
    bulk.saveState.rowErrors.size > 0 && bulk.saveState.lastSaveAttemptCount > 0
      ? t("realestate.override.edit_mode.counter_partial", {
          saved: bulk.saveState.lastSaveAttemptCount - bulk.saveState.rowErrors.size,
          total: bulk.saveState.lastSaveAttemptCount,
        })
      : t("realestate.override.edit_mode.counter", { count: bulk.changesCount });

  return (
    <div className="space-y-2">
      <header className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold">{t("realestate.detail.show_schedule")}</h4>
        <div className="flex gap-2 items-center">
          {!bulk.editMode && (
            <>
              <button
                type="button"
                className="text-sm px-2 py-1 border rounded"
                onClick={bulk.enterEditMode}
              >
                {t("realestate.override.edit_mode.enter")}
              </button>
              <button
                type="button"
                className="text-sm px-2 py-1 border rounded"
                onClick={() => setAddModalOpen(true)}
              >
                {t("realestate.override.add")}
              </button>
            </>
          )}
          {bulk.editMode && <span className="text-xs text-slate-600">{counterText}</span>}
        </div>
      </header>

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left border-b">
            <th className="p-2">{t("realestate.amortization.month")}</th>
            <th className="p-2">{t("realestate.amortization.date")}</th>
            <th className="p-2 text-right">{t("realestate.amortization.payment")}</th>
            <th className="p-2 text-right">{t("realestate.amortization.interest")}</th>
            <th className="p-2 text-right">{t("realestate.amortization.principal")}</th>
            <th className="p-2 text-right">{t("realestate.amortization.prepayment")}</th>
            <th className="p-2 text-right">{t("realestate.amortization.remaining")}</th>
            {bulk.editMode && <th className="p-2" />}
          </tr>
        </thead>
        <tbody>
          {schedule.map((entry) => {
            const ym = entry.date.slice(0, 7);
            const draft = bulk.rows.get(ym);
            const override = findOverride(entry, overrides);
            const hasOverride = override !== null;
            const rowError = bulk.saveState.rowErrors.get(ym) ?? null;
            const rowBg = hasOverride
              ? "bg-amber-50"
              : entry.prepaymentCents > 0
                ? "bg-amber-50"
                : "";
            const errBorder = rowError ? "border-red-500 border" : "";
            const phaseMarker = isPhaseStart(ym);
            const phaseMarkerClass = phaseMarker ? "border-t-2 border-amber-400" : "";

            return (
              <tr
                key={entry.month}
                className={`border-b ${rowBg} ${errBorder} ${phaseMarkerClass}`}
              >
                <td className="p-2 font-mono text-xs">
                  {entry.month}
                  {hasOverride && (
                    <span className="ml-2 text-xs px-1 bg-amber-100 text-amber-800 rounded">
                      {t("realestate.override.badge")}
                    </span>
                  )}
                  {phaseMarker && (
                    <span className="ml-2 text-xs px-1 bg-amber-100 text-amber-800 rounded">
                      {t("realestate.loan.interest_phase.schedule_phase_marker", {
                        n: phaseMarker.idx,
                        date: formatMonthShort(phaseMarker.phase.startDate),
                      })}
                    </span>
                  )}
                </td>
                <td className="p-2">{formatDate(entry.date)}</td>
                <td className="p-2 text-right font-mono">{formatEur(entry.paymentCents)}</td>
                <td className="p-2 text-right font-mono">
                  {bulk.editMode && draft ? (
                    <MoneyInput
                      valueCents={draft.interestCents}
                      onChange={(cents) => bulk.setRow(ym, { interestCents: cents })}
                      placeholder={(entry.interestCents / 100).toFixed(2)}
                      className="w-24 border rounded px-1 py-0.5 text-right font-mono"
                    />
                  ) : (
                    formatEur(entry.interestCents)
                  )}
                </td>
                <td className="p-2 text-right font-mono">
                  {bulk.editMode && draft ? (
                    <MoneyInput
                      valueCents={draft.principalCents}
                      onChange={(cents) => bulk.setRow(ym, { principalCents: cents })}
                      placeholder={(entry.principalCents / 100).toFixed(2)}
                      className="w-24 border rounded px-1 py-0.5 text-right font-mono"
                    />
                  ) : (
                    formatEur(entry.principalCents)
                  )}
                </td>
                <td className="p-2 text-right font-mono">
                  {entry.prepaymentCents > 0 ? formatEur(entry.prepaymentCents) : "—"}
                </td>
                <td className="p-2 text-right font-mono">{formatEur(entry.principalAtEnd)}</td>
                {bulk.editMode && (
                  <td className="p-2 text-right">
                    {hasOverride && (
                      <button
                        type="button"
                        onClick={() => bulk.resetRow(ym)}
                        className="text-xs text-red-600 underline"
                        title={t("realestate.override.reset")}
                      >
                        ✕
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {bulk.editMode && (
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" className="px-3 py-1 border rounded" onClick={bulk.cancelEdit}>
            {t("realestate.override.edit_mode.cancel")}
          </button>
          <button
            type="button"
            onClick={bulk.save}
            disabled={bulk.changesCount === 0 || bulk.saveState.loading}
            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
            title={
              bulk.changesCount === 0 ? t("realestate.override.edit_mode.no_changes_hint") : ""
            }
          >
            {bulk.saveState.loading
              ? t("realestate.override.edit_mode.save_loading")
              : t("realestate.override.edit_mode.save")}
          </button>
        </div>
      )}

      {bulk.needsDiscardConfirmation && (
        <ConfirmDialog
          open
          title={t("realestate.override.edit_mode.discard_confirm_title")}
          message={t("realestate.override.edit_mode.discard_confirm_message", {
            count: bulk.changesCount,
          })}
          confirmLabel={t("realestate.override.edit_mode.discard_confirm_ok")}
          destructive
          onConfirm={() => bulk.confirmDiscard()}
          onCancel={() => bulk.abortDiscardConfirmation()}
        />
      )}

      {addModalOpen && (
        <ScheduleOverrideFormModal
          open
          onClose={() => setAddModalOpen(false)}
          initial={null}
          onSubmit={async (input) => {
            await upsertOverride.mutateAsync({ propertyId, loanId, input });
            setAddModalOpen(false);
          }}
          submitting={upsertOverride.isPending}
        />
      )}
    </div>
  );
}
